import {
  Button,
  ResponsiveModalContent,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
} from '@bridebook/aura';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { interactedWithGuestCommsAction } from '@bridebook/web/lib/inbox/guests/analytics/actions';
import { useCreateMessageFlow } from '@bridebook/web/lib/inbox/guests/create-message-flow/create-message-flow-context';
import { getRecipientsStrings } from '@bridebook/web/lib/inbox/guests/guests-helpers';
import { useCreateCampaign } from '@bridebook/web/lib/inbox/hooks/campaigns/use-email-campaigns';
import { useSendTestMessage } from '@bridebook/web/lib/inbox/hooks/campaigns/use-send-test-message';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { useWeddingWebsiteUrl } from '@bridebook/web/lib/wedding-website/hooks/use-wedding-website-url';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { CampaignSummary } from '../../campaign-summary/campaign-summary';
import { SendTestEmailModal } from './send-test-email-modal';

export const Summary = () => {
  const { t } = useTranslation('inbox');
  const { goToPreviousStep, formData, forceCloseModal } = useCreateMessageFlow();
  const dispatch = useDispatch();
  const router = useRouter();
  const { createEmailCampaign } = useCreateCampaign();
  const { isMobile } = useSelector(getScreenType);

  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async () => {
    setIsLoading(true);
    const response = await createEmailCampaign({
      ...formData.message,
      recipients: formData.recipients.guests.map((guest) => guest.id),
    });

    forceCloseModal();
    setIsLoading(false);
    dispatch(
      interactedWithGuestCommsAction({
        action: 'clicked',
        location: 'guestComms',
        section: 'messageSummary',
        element: 'continueButton',
        elementLabel: 'Send to guests',
      }),
    );

    if (response.success && response.data) {
      dispatch(
        toggleSnackbar('success', t('guests.createMessageFlow.summary.successMessage'), undefined, {
          icon: '🎉',
        }),
      );
      const redirectTo = isMobile
        ? UrlHelper.inbox.guests.main
        : UrlHelper.inbox.guests.campaign(response.data.campaignId);
      router.push(redirectTo);
    } else {
      router.push(UrlHelper.inbox.guests.main);
    }
  };

  const { websiteUrl: weddingWebsiteUrl } = useWeddingWebsiteUrl();

  const [isSendTestEmailModalOpen, setIsSendTestEmailModalOpen] = useState(false);
  const recipientsStrings = useMemo(
    () => getRecipientsStrings(formData.recipients.guests),
    [formData.recipients.guests],
  );

  const handleSendTestEmailClick = () => {
    setIsSendTestEmailModalOpen(true);
    dispatch(
      interactedWithGuestCommsAction({
        action: 'clicked',
        location: 'guestComms',
        section: 'messageSummary',
        element: 'testEmailButtonSend',
        elementLabel: 'Send test email',
      }),
    );
  };
  const { createEmailCampaign: sendTestEmail, isLoading: isSendTestEmailLoading } =
    useSendTestMessage();

  const handleSendTestEmail = () => sendTestEmail(formData.message);

  return (
    <>
      <ResponsiveModalHeader onBackButtonClick={goToPreviousStep}>
        {t('guests.createMessageFlow.summary.header')}
      </ResponsiveModalHeader>
      <ResponsiveModalContent className="p-0">
        <div className="flex w-full flex-col gap-0 xl:gap-4 xl:px-[100px]">
          <CampaignSummary
            recipientsAccordionDefaultOpen={true}
            recipients={recipientsStrings}
            subject={formData.message.subject}
            message={formData.message.message}
            includeGuestsNames={formData.message.includeGuestsNames}
            guestName={
              formData.message.includeGuestsNames ? t('guests.messageSummary.guestName') : ''
            }
            websiteUrl={formData.message.shareWeddingWebsiteLink ? weddingWebsiteUrl : ''}
            displayPassword={formData.message.displayPasswordInEmail}
          />
        </div>
      </ResponsiveModalContent>
      <ResponsiveModalFooter className="border-t border-space-15">
        <div className="mx-auto flex w-full flex-wrap-reverse gap-3">
          <Button
            variant="secondary"
            disabled={isLoading || isSendTestEmailLoading}
            onClick={handleSendTestEmailClick}
            className="flex-1">
            {t('guests.createMessageFlow.summary.sendTestEmail')}
          </Button>

          <Button
            variant="primary"
            onClick={handleSend}
            loading={isLoading}
            disabled={isLoading}
            className="flex-1">
            {t('guests.createMessageFlow.summary.sendToGuests')}
          </Button>
        </div>
      </ResponsiveModalFooter>
      <SendTestEmailModal
        onSend={handleSendTestEmail}
        isOpen={isSendTestEmailModalOpen}
        onClose={() => setIsSendTestEmailModalOpen(false)}
      />
    </>
  );
};
